import { round } from 'lodash';
import { getOfferDescription } from './utils';
import EN from '../../../../EN.json';
import FR from '../../../../FR.json';

export const getSummaryDetailsForFieldWithProductCatalog = field => {
  const summary = [];

  if (!field || field.data.length === 0) return summary;

  field.data.forEach(fieldItem => {
    fieldItem.offerDetails.forEach(item => {
      summary.push({
        term: field.data[0].term,
        offerDetailsEn: item.offerDetailsEn,
        offerDetailsFr: item.offerDetailsFr,
        oneTimeCharges:
          item.frequency === 'oneTime' ? Number(item.value) || 0 : 0,
        monthlyCharges:
          item.frequency === 'monthly' ? Number(item.value) || 0 : 0,
        threeYearEasyPaymentCharges: 0,
        fiveYearEasyPaymentCharges: 0
      });
    });
  });
  return summary;
};

export const getSummaryDetailsForFieldWithProductCatalogForPriceSummary = (
  field,
  isAlternative,
  locale
) => {
  const summary = [];

  if (!field || field.data.length === 0) return summary;

  // Determine target language based on locale and isAlternative
  const isTargetFrench = locale === 'fr-CA' ? !isAlternative : isAlternative;
  const translations = isTargetFrench ? FR : EN;
  const { termLabel } = translations.vulcanWebform.summaryConfig;

  // Translate term description when target language is French
  // field.term is always in English from the API, so we map it at display level
  const frenchTermMap = {
    'Month to Month': 'De mois en mois',
    'Month to month': 'De mois en mois',
    'month to month': 'De mois en mois',
    monthly: 'mensuel',
    '36 Months': 'Durée de 36 mois',
    '60 Months': 'Durée de 60 mois'
  };
  const termDescription = isTargetFrench
    ? frenchTermMap[field.term] || field.term
    : field.term;

  summary.push({
    label: termLabel,
    description: termDescription
  });

  field.data.forEach(fieldItem => {
    fieldItem.offerDetails.forEach(item => {
      summary.push({
        description: getOfferDescription(item, locale, isAlternative),
        oneTimeCharges:
          item.frequency === 'oneTime' ? Number(item.value) || 0 : 0,
        monthlyCharges:
          item.frequency === 'monthly' ? Number(item.value) || 0 : 0,
        threeYearEasyPaymentCharges: 0,
        fiveYearEasyPaymentCharges: 0
      });
    });
  });
  return summary;
};

const getFieldSummaryDetailsForOneOption = (field, option) => {
  const fieldSummary = {
    qty: 0,
    monthlyCharges: 0,
    oneTimeCharges: 0,
    threeYearEasyPaymentCharges: 0,
    fiveYearEasyPaymentCharges: 0
  };
  if (!field) return fieldSummary;
  if (option && field.value && field.value.includes(option)) {
    const valueInOptions = field.options.find(
      optionItem => optionItem.fields.value === option
    );
    if (valueInOptions) {
      fieldSummary.qty = 1;
      const selectedValue = valueInOptions.fields;
      fieldSummary.monthlyCharges = selectedValue.monthlyPayment;
      fieldSummary.oneTimeCharges = selectedValue.oneTimePrice || 0;
      fieldSummary.threeYearEasyPaymentCharges =
        selectedValue.threeYearEasyPayment || 0;
      fieldSummary.fiveYearEasyPaymentCharges =
        selectedValue.fiveYearEasyPayment || 0;
    }
  }
  return fieldSummary;
};

export const getSummaryDetailsForFieldWithSelectedOptions = (
  field,
  addData
) => {
  const summary = [];

  if (!field || !field.type) return summary;

  switch (field.type) {
    case 'Radio':
    case 'MultiCheckbox':
      (field.value || []).forEach(fieldItem => {
        const valueInOptions = field.options.find(
          optionItem => optionItem.fields.value === fieldItem
        );
        if (valueInOptions) {
          const selectedValue = valueInOptions.fields;
          summary.push({
            description: fieldItem,
            oneTimeCharges: selectedValue.oneTimePrice || 0,
            monthlyCharges: selectedValue.monthlyPayment || 0,
            threeYearEasyPaymentCharges:
              selectedValue.threeYearEasyPayment || 0,
            fiveYearEasyPaymentCharges: selectedValue.fiveYearEasyPayment || 0
          });
        }
      });
      break;

    default:
      break;
  }

  if (addData && addData.length > 0) {
    summary.push(...addData);
  }

  return summary;
};

export const getSummaryDetailsForFieldWithCouponCode = field => {
  const summary = [];

  if (!field || !field.type) return summary;

  switch (field.type) {
    case 'CouponBasedDiscount':
      field.redeemOfferSelection.forEach(fieldItem => {
        let oneTimePrice;
        let monthlyPrice;
        if (fieldItem.duration === 'One-Time') {
          oneTimePrice = round(fieldItem.promotionAmount, 2);
        } else {
          monthlyPrice = round(fieldItem.promotionAmount, 2);
        }
        summary.push({
          duration: fieldItem.duration,
          couponCode: fieldItem.couponCode,
          promotionAmount: fieldItem.promotionAmount,
          validationCode: fieldItem.validationCode,
          oneTimeCharges: -oneTimePrice || 0,
          monthlyCharges: -monthlyPrice || 0,
          threeYearEasyPaymentCharges: 0,
          fiveYearEasyPaymentCharges: 0
        });
      });
      break;

    default:
      break;
  }
  return summary;
};

export const getFieldSummaryDetailsFromField = field => {
  const summary = {
    qty: '',
    description: '',
    monthlyCharges: 0,
    oneTimeCharges: 0,
    threeYearEasyPaymentCharges: 0,
    fiveYearEasyPaymentCharges: 0
  };

  if (!field) return summary;
  switch (field.type) {
    case 'Radio':
      if (!field.value) return summary;
      // eslint-disable-next-line no-case-declarations
      const selectedOption = field.options.find(
        option => option.fields.value === field.value
      );
      if (selectedOption) {
        summary.qty = '1';
        summary.description = selectedOption.fields.label;
        summary.monthlyCharges += selectedOption.fields.monthlyPayment || 0;
        summary.oneTimeCharges += selectedOption.fields.oneTimePrice || 0;
        summary.threeYearEasyPaymentCharges +=
          selectedOption.fields.threeYearEasyPayment || 0;
        summary.fiveYearEasyPaymentCharges +=
          selectedOption.fields.fiveYearEasyPayment || 0;
      }
      break;
    case 'PlusMinusInput':
      summary.qty = field.value;
      summary.description = '';
      summary.monthlyCharges += field.monthlyPayment * +field.value || 0;
      summary.oneTimeCharges += field.oneTimePrice * +field.value || 0;
      summary.threeYearEasyPaymentCharges +=
        field.threeYearEasyPayment * +field.value || 0;
      summary.fiveYearEasyPaymentCharges +=
        field.fiveYearEasyPayment * +field.value || 0;
      summary.unitPrice =
        field.oneTimePrice ||
        field.monthlyPayment ||
        field.threeYearEasyPayment ||
        field.fiveYearEasyPayment;
      break;

    case 'Number':
      summary.qty = '';
      summary.description = '';
      summary.monthlyCharges += (field.monthlyPayment || 0) * +field.value || 0;
      summary.oneTimeCharges += (field.oneTimePrice || 0) * +field.value || 0;
      summary.threeYearEasyPaymentCharges +=
        (field.threeYearEasyPayment || 0) * +field.value || 0;
      summary.fiveYearEasyPaymentCharges +=
        (field.fiveYearEasyPayment || 0) * +field.value || 0;
      break;

    case 'Money':
      summary.qty = '';
      summary.description = '';
      summary.monthlyCharges += field.monthlyPayment || 0;
      summary.oneTimeCharges += field.oneTimePrice || 0;
      summary.threeYearEasyPaymentCharges += field.threeYearEasyPayment || 0;
      summary.fiveYearEasyPaymentCharges += field.fiveYearEasyPayment || 0;
      break;

    default:
      break;
  }
  return summary;
};

export default getFieldSummaryDetailsForOneOption;
